<template>
  <div class="waymap">
    <div ref="mapEl" class="waymap__map"></div>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { featureLayer } from 'esri-leaflet';
import { markRaw } from "vue";

// Vue 3's reactive() wraps every property in a Proxy. Leaflet objects
// (Map, Marker, Layer, …) hold internal state like `_map`, `_events`,
// `_latlng` that Leaflet mutates directly; the Proxy breaks Leaflet's
// internal `delete` semantics on cleanup, leaving zombie markers still
// subscribed to map events that crash with
//   "Cannot read properties of null (reading 'latLngToLayerPoint')"
// on the next zoom/move. markRaw() flags an object so Vue's reactivity
// system leaves it alone — we wrap EVERY Leaflet object before storing
// it on `this`.

import { toLatLng } from "/src/services/coords";
import { createMarker } from "/src/utils/markerFactory";
import proj4 from "proj4";
import { useSettingsStore } from "/src/stores/settings";
import { getLookupDataByType } from "@/api/lookups";

export default {
  name: "WayMap",
  components: { createMarker, toLatLng },
  props: {
    items: { type: Array, default: () => [] },
    highlightId: { type: [String, Number, null], default: null },
    typeStyles: {
      type: Object,
      default: () => ({
        task: { color: "#2a72cf", icon: "fas fa-tasks" },
        finding: { color: "#f2994a", icon: "fas fa-exclamation-circle" },
        inspection: { color: "#27ae60", icon: "fas fa-clipboard-check" },
      }),
    },
    selectedCrs: { type: String, default: "EPSG:4326" },
    showAccuracy: { type: Boolean, default: false },
    initialCenter: { type: Array, default: () => [53.35, -6.26] },
    initialZoom: { type: Number, default: 7 },
    tileUrl: {
      type: String,
      default: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    },
    tileAttribution: {
      type: String,
      default: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>',
    },
    geojson: { type: [Object, null], default: null },
    geoHoverId: { type: [String, Number, null], default: null },
    showZoneLabels: { type: Boolean, default: true },
    tileLayer: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      map: null,
      rootGroup: null,
      // Containers that hold Leaflet objects are themselves markRaw — Vue
      // never wraps the inner entries, so set/get behave as plain JS Maps.
      markersById: markRaw(new Map()), // id -> { marker, accuracyCircle }
      rebuildTimer: null,
      isDestroyed: false,
      activeId: null,
      geojsonLayer: null,
      geoLayerById: markRaw(new Map()), // zoneId-> Leaflet layer (L.Path)
      geoLabelById: markRaw(new Map()), // zoneId-> Leaflet label (L.Marker with divIcon)
      activeShapeId: null,
      settingsStore: null,
      currentTileLayer: null,
      featureLayers: markRaw({}), // Store feature layers by lookupDataId
      featureLayersById: markRaw({}), // Store feature layers by lookupDataId for quick access
      featureLayersLoaded: false, // Flag to track if layers are loaded
      pendingFeatureLayerUpdates: [], // Queue for updates before layers are loaded
      mapReadyForZoom: false, // Flag to track if map is ready for zoom operations
      isInitialLoad: true, // Flag to track if this is the first marker load
      // Track first geojson (zones) load separately. Subsequent re-loads
      // — triggered by feature-layer toggles, settings changes, etc. —
      // must NOT call fitBounds, or the user's manual zoom is yanked
      // back to the zones' extent every time they tick a checkbox.
      isInitialGeojsonLoad: true,
    };
  },
  created() {
    // Pinia store instance
    this.settingsStore = useSettingsStore();
  },
  mounted() {
    this.initMap();
    // Wait for map to be ready before doing anything else
    this.map.whenReady(() => {
      this.map._loaded = true;
      this.rebuildMarkers();
      this.loadGeojson(this.geojson);
      // Load feature layers after map is ready
      setTimeout(() => {
        this.loadFeatureLayers();
      }, 100);
      this.$emit("layer-ready", this.map);
    });
    this.attachZoomHotkeys();
  },

  beforeUnmount() {
    this.isDestroyed = true;
    if (this.rebuildTimer) clearTimeout(this.rebuildTimer);
    this.rebuildTimer = null;
    if (this._stepWheelCleanup) {
      this._stepWheelCleanup();
      this._stepWheelCleanup = null;
    }
    if (this._zoomHotkeyCleanup) {
      this._zoomHotkeyCleanup();
      this._zoomHotkeyCleanup = null;
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    if (this._invalidateTimer) {
      clearTimeout(this._invalidateTimer);
      this._invalidateTimer = null;
    }
    if (this.geojsonLayer) this.geojsonLayer.removeFrom(this.map);
    // Remove feature layers
    Object.values(this.featureLayers).forEach(layer => {
      if (layer && this.map) {
        this.map.removeLayer(layer);
      }
    });
    if (this.rootGroup && this.map) {
      this.map.removeLayer(this.rootGroup);
    }
    if (this.map) this.map.remove();
    this.map = null;
    this.rootGroup = null;
    this.markersById.clear();
    this.featureLayers = {};
    this.featureLayersById = {};
  },

  watch: {
    items() {
      this.scheduleRebuild();
    },
    selectedCrs() {
      this.scheduleRebuild();
    },
    showAccuracy() {
      this.scheduleRebuild();
    },
    typeStyles: {
      deep: true,
      handler() {
        this.scheduleRebuild();
      },
    },
    highlightId(id) {
      this.setActive(id);
    }, // <-- react to parent hover
    geojson(newVal) {
      if (this.map) {
        this.map.whenReady(() => {
          this.loadGeojson(newVal);
        });
      } else {
        // Map not ready yet, wait a bit
        setTimeout(() => {
          if (this.map) {
            this.map.whenReady(() => {
              this.loadGeojson(newVal);
            });
          }
        }, 100);
      }
    },
    showZoneLabels(newVal) {
      // Toggle labels when prop changes
      if (this.map) {
        this.geoLabelById.forEach((label) => {
          if (newVal) {
            if (!this.map.hasLayer(label)) {
              label.addTo(this.map);
            }
          } else {
            if (this.map.hasLayer(label)) {
              this.map.removeLayer(label);
            }
          }
        });
      }
    },
    geoHoverId(id) {
      this.setActiveShape(id);
    },
    tileLayer(newTileLayer) {
      if (!this.map || !newTileLayer) return;

      // Validate the incoming URL — if it's missing or doesn't look like a
      // tile-template URL we keep the existing (working) layer rather than
      // swapping it out and leaving the map blank.
      const url = (newTileLayer.url || "").trim();
      const looksLikeTileUrl =
        url.startsWith("http") && url.includes("{z}") && url.includes("{x}") && url.includes("{y}");
      if (!looksLikeTileUrl) {
        return;
      }

      // Swap the URL in-place rather than removing + re-adding. The latter
      // can leave Leaflet's zoom animator holding a stale reference to the
      // detached layer's DOM, which manifests as a "_leaflet_pos undefined"
      // error and a blank/grey viewport on the next zoom.
      if (this.currentTileLayer && typeof this.currentTileLayer.setUrl === "function") {
        this.currentTileLayer.options.attribution = newTileLayer.attribution;
        this.currentTileLayer.setUrl(url);
        return;
      }

      // First-time set (no existing layer) — create one.
      this.currentTileLayer = markRaw(L.tileLayer(url, {
        maxZoom: 19,
        attribution: newTileLayer.attribution,
        keepBuffer: 4,
      }).addTo(this.map));
      this.attachTileRetry(this.currentTileLayer);
    },
  },

  methods: {
    initMap() {
      const map = L.map(this.$refs.mapEl, {
        zoomControl: false,
        preferCanvas: true,
        zoomAnimation: false, // Disable zoom animation until map is fully ready
        // Explicitly enable all zoom methods
        // scrollWheelZoom disabled here — replaced with a custom one-level-per-
        // notch handler (see attachStepWheelZoom) so the wheel matches the +/-
        // buttons instead of Leaflet's accelerated 2+ level jumps.
        scrollWheelZoom: false,
        doubleClickZoom: true,
        touchZoom: true,
        boxZoom: true,
        dragging: true,
        minZoom: 1,
        maxZoom: 20,
      }).setView(this.initialCenter, this.initialZoom);

      // Leaflet's built-in keyboard handler also zooms on +/- (centre-anchored,
      // and additive to our hotkey → a doubled, offset zoom). Strip its zoom
      // keys so only our stepZoom-based hotkeys handle +/-; arrow-key panning
      // stays intact.
      if (map.keyboard) {
        map.keyboard._zoomKeys = {};
      }

      // Use provided tile layer or default — fall back to the OSM default if
      // the supplied tile config has a missing/invalid URL.
      const candidate = this.tileLayer || {
        url: this.tileUrl,
        attribution: this.tileAttribution,
      };
      const candidateUrl = (candidate.url || "").trim();
      const validTileUrl =
        candidateUrl.startsWith("http") &&
        candidateUrl.includes("{z}") &&
        candidateUrl.includes("{x}") &&
        candidateUrl.includes("{y}");
      const tileConfig = validTileUrl
        ? candidate
        : { url: this.tileUrl, attribution: this.tileAttribution };

      L.control.zoom({ position: "topleft" }).addTo(map);
      this.rebindZoomControl(map);

      this.currentTileLayer = markRaw(L.tileLayer(tileConfig.url, {
        maxZoom: 20,
        attribution: tileConfig.attribution,
        keepBuffer: 4,
      }).addTo(map));
      this.map = markRaw(map);
      this.attachTileRetry(this.currentTileLayer);
      this.attachStepWheelZoom(map);

      // The container usually isn't at its final size on first mount — flex/grid
      // layout settles a tick later. Leaflet caches the stale (small) size at
      // setView and only requests tiles for that area, so the map looks
      // half-drawn (a small square of tiles, the rest grey) until something
      // forces a resize. Invalidate once layout settles, and keep watching the
      // container so later resizes (side panels, window) re-fill tiles too.
      this._scheduleInvalidate();
      if (typeof ResizeObserver !== "undefined") {
        this._resizeObserver = new ResizeObserver(() => this._scheduleInvalidate());
        this._resizeObserver.observe(this.$refs.mapEl);
      }

      map.whenReady(() => {
        this.map._loaded = true;
        // Emit map click for weather popup etc. (fires when clicking empty
        // map, not markers/shapes).
        map.on('click', (e) => {
          this.$emit('map-click', { lat: e.latlng.lat, lng: e.latlng.lng });
        });
      });

      // Clustering disabled — use plain layer group regardless of props.
      this.rootGroup = markRaw(L.layerGroup());

      this.rootGroup.addTo(this.map);

      // Wait for map to be fully loaded before allowing feature layers
      this.map.whenReady(() => {
        this.map._loaded = true;
      });
    },

    // Google-Maps-style scroll zoom: exactly one zoom level per wheel notch,
    // anchored on the cursor. Matches the +/- buttons (which step by 1) instead
    // of Leaflet's velocity-based wheel zoom that jumps 2+ levels on a fast
    // scroll. A single physical notch can emit a burst of wheel events, so we
    // throttle to one zoom step per ~140ms.
    attachStepWheelZoom(map) {
      const container = map.getContainer();
      let lastZoomTime = 0;
      const onWheel = (e) => {
        L.DomEvent.preventDefault(e);
        L.DomEvent.stopPropagation(e);
        const now = Date.now();
        if (now - lastZoomTime < 140) return;
        lastZoomTime = now;
        const dir = e.deltaY < 0 ? 1 : -1;
        // Anchor on the cursor so the point under the pointer stays put.
        this.stepZoom(dir, map.mouseEventToContainerPoint(e));
      };
      // Track the last pointer position over the map so the +/- buttons and
      // hotkeys can anchor on the same spot the wheel would, instead of the
      // viewport centre — keeps the view "held" wherever the user last hovered.
      const onMove = (e) => {
        this._lastPointerPoint = map.mouseEventToContainerPoint(e);
      };
      L.DomEvent.on(container, "wheel", onWheel, this);
      L.DomEvent.on(container, "mousemove", onMove, this);
      this._stepWheelCleanup = () => {
        L.DomEvent.off(container, "wheel", onWheel, this);
        L.DomEvent.off(container, "mousemove", onMove, this);
      };
    },

    // Single source of truth for one-level zoom steps, shared by the wheel,
    // the +/- hotkeys and the +/- buttons (via the exposed methods below).
    // Always snaps to an integer level and keeps `anchor` (a container Point)
    // fixed. When no anchor is given it uses the last pointer position over the
    // map (so +/- holds the same spot as the wheel), falling back to centre.
    // Coalesce invalidateSize calls — a burst of ResizeObserver ticks during a
    // layout animation shouldn't trigger a tile reload per frame.
    _scheduleInvalidate() {
      if (this._invalidateTimer) clearTimeout(this._invalidateTimer);
      this._invalidateTimer = setTimeout(() => {
        if (this.map) this.map.invalidateSize({ animate: false });
      }, 60);
    },

    stepZoom(delta, anchor) {
      // Guard against an early wheel/key event before the map has finished
      // loading — zooming a not-yet-ready map can surface a null-layer
      // getCenter() deep in Leaflet's move handling.
      if (!this.map || !this.map._loaded) return;
      const map = this.map;
      const target = Math.round(map.getZoom()) + delta;
      // Same mechanism for all three (proven by the wheel): setZoomAround keeps a
      // point fixed. Wheel passes the cursor; buttons/keys pass nothing, so we
      // anchor on the viewport centre — predictable, no jump.
      const point = anchor || map.getSize().divideBy(2);
      map.setZoomAround(point, target, { animate: false });
    },

    // Public API used by keyboard shortcuts and parent overlay buttons.
    zoomByKeyboardStep(delta) {
      this.stepZoom(delta);
    },
    zoomInStep() {
      this.zoomByKeyboardStep(1);
    },
    zoomOutStep() {
      this.zoomByKeyboardStep(-1);
    },

    // Leaflet's built-in zoom control (+/- top-left) calls map.zoomIn/zoomOut,
    // which are centre-anchored and additive on the current (possibly
    // fractional) zoom — so they zoom with an offset instead of holding the
    // pointer the way the wheel does. Replace each button's click handler with
    // our shared stepZoom. cloneNode strips Leaflet's original listener.
    rebindZoomControl(map) {
      const container = map.getContainer();
      const rebind = (selector, delta) => {
        const btn = container.querySelector(selector);
        if (!btn || !btn.parentNode) return;
        const fresh = btn.cloneNode(true);
        btn.parentNode.replaceChild(fresh, btn);
        L.DomEvent.disableClickPropagation(fresh);
        L.DomEvent.on(
          fresh,
          "click",
          (e) => {
            L.DomEvent.stop(e);
            this.stepZoom(delta);
          },
          this
        );
      };
      rebind(".leaflet-control-zoom-in", 1);
      rebind(".leaflet-control-zoom-out", -1);
    },

    // Keyboard shortcuts: "+"/"=" zoom in, "-"/"_" zoom out (one level each,
    // matching the +/- buttons and the wheel). Ignored while typing in an
    // input/textarea/select or contenteditable so it doesn't hijack the keys.
    attachZoomHotkeys() {
      const onKeyDown = (e) => {
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        const el = e.target;
        const tag = el && el.tagName ? el.tagName.toUpperCase() : "";
        if (
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          (el && el.isContentEditable)
        ) {
          return;
        }
        if (!this.map) return;
        if (e.key === "+" || e.key === "=" || e.code === "NumpadAdd") {
          e.preventDefault();
          this.zoomByKeyboardStep(1);
        } else if (e.key === "-" || e.key === "_" || e.code === "NumpadSubtract") {
          e.preventDefault();
          this.zoomByKeyboardStep(-1);
        }
      };
      window.addEventListener("keydown", onKeyDown);
      this._zoomHotkeyCleanup = () =>
        window.removeEventListener("keydown", onKeyDown);
    },

    scheduleRebuild() {
      if (this.isDestroyed || !this.map) return;
      if (this.rebuildTimer) clearTimeout(this.rebuildTimer);
      this.rebuildTimer = setTimeout(() => {
        if (!this.isDestroyed) {
          this.rebuildMarkers();
        }
      }, 120);
    },

    attachTooltip(item, marker) {
      try {
        const title = this.escapeHtml(item.title || "Untitled");
        const type = this.escapeHtml(item.type || "");
        const zone = item.zoneName ? ` &middot; ${this.escapeHtml(item.zoneName)}` : "";
        const statusRow = item.status
          ? `
        <div class="wm-tip-row">
          <span class="wm-tip-label">Status</span>
          <span class="wm-tip-val">${this.escapeHtml(String(item.statusFK))}</span>
        </div>`
          : "";

        let coordsRows = "";
        if (Number.isFinite(item.lat) && Number.isFinite(item.lng)) {
          // WGS84 row
          const wgs84 = `${this.fmt(item.lat)}, ${this.fmt(item.lng)}`;
          // Projected row from settings
          const projected = this.projectedStringFromSettings(item.lat, item.lng, 6, 2);
          coordsRows = `
        <div class="wm-tip-row">
          <span class="wm-tip-label">WGS84</span>

        </div> <span class="wm-tip-val mono">${this.escapeHtml(wgs84)}</span>
        ${
          projected
            ? `
        <div class="wm-tip-row">
          <span class="wm-tip-label">${this.escapeHtml(
            this.settingsStore?.crs?.name || "Projected"
          )}</span>

        </div>
        <div class="wm-tip-val mono">${this.escapeHtml(projected)}</div>
        `
            : ""
        }`;
        }

        const html = `
      <div class="wm-tip-card">
        <div class="wm-tip-title">${title}</div>
        <div class="wm-tip-sub">
          <span class="wm-badge-mini ${type}">${type}</span>${zone}
        </div>
        ${statusRow}
        ${coordsRows}
      </div>`;

        marker.bindTooltip(html, {
          direction: "top",
          offset: L.point(0, -18),
          permanent: false,
          sticky: true,
          opacity: 1,
          className: "wm-tip",
        });

        const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
        if (isTouch) {
          marker.on("click", () => marker.openTooltip());
          // Use a namespaced event to allow cleanup - store handler reference on marker
          const closeTooltipHandler = (e) => {
            if (!e.latlng || !marker.getLatLng().equals(e.latlng)) marker.closeTooltip();
          };
          this.map.on("click.tooltip", closeTooltipHandler);
          // Store handler reference for cleanup
          marker._tooltipCloseHandler = closeTooltipHandler;
        } else {
          marker.on("mouseover", () => marker.openTooltip());
          marker.on("mouseout", () => marker.closeTooltip());
        }
      } catch (err) {
      }
    },

    rebuildMarkers(forceRecreateGroup = false) {
      if (this.isDestroyed || !this.map) return;

      // Always fully recreate the cluster group to avoid stale internal state
      if (this.rootGroup) {
        this.map.removeLayer(this.rootGroup);
        this.rootGroup = null;
      }

      // Ensure rootGroup exists before using it
      if (!this.rootGroup) {
        if (!this.map) return; // Component was destroyed
        // Clustering disabled — always use plain layer group.
        this.rootGroup = markRaw(L.layerGroup());
        if (!this.map) return; // Component was destroyed during creation
        this.rootGroup.addTo(this.map);
      }

      if (!this.rootGroup || !this.map) return; // Final safety check
      
      // Double-check rootGroup exists right before use (race condition protection)
      if (!this.rootGroup) return;
      
      // Properly clean up all markers and accuracy circles before clearing
      this.markersById.forEach((bundle) => {
        // Remove event listeners from markers
        bundle.marker.off();
        // Clean up tooltip close handler if it exists
        if (bundle.marker._tooltipCloseHandler && this.map) {
          this.map.off("click.tooltip", bundle.marker._tooltipCloseHandler);
          delete bundle.marker._tooltipCloseHandler;
        }
        // Remove accuracy circles if they exist
        if (bundle.accuracyCircle) {
          bundle.accuracyCircle.off();
          if (this.map && this.map.hasLayer(bundle.accuracyCircle)) {
            this.map.removeLayer(bundle.accuracyCircle);
          }
        }
        // Remove marker from map if it exists (though clearLayers should handle this)
        if (this.map && this.map.hasLayer(bundle.marker)) {
          this.map.removeLayer(bundle.marker);
        }
      });
      
      // Also clean up any orphaned map click listeners
      if (this.map) {
        this.map.off("click.tooltip");
      }
      
      this.rootGroup.clearLayers();
      this.markersById.clear();

      const normalized = this.items.map((it) => this.normalizeItem(it)).filter(Boolean);

      // Deduplicate by type/id/coords to prevent ghost markers on rebuild
      const seen = new Set();
      const deduped = normalized.filter((item) => {
        const key = `${item.type || "unknown"}|${item.id ?? ""}|${item.lat}|${item.lng}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      deduped.forEach((item) => {
        const bundle = createMarker(item, this.typeStyles, {
          showAccuracy: this.showAccuracy,
        });

        // hover/click events → parent + local highlight
        bundle.marker.on("mouseover", () => {
          this.$emit("marker-hover", { id: item.id, type: item.type, item });
          this.setActive(item.id);
        });
        bundle.marker.on("mouseout", () => {
          this.$emit("marker-leave", { id: item.id, type: item.type, item });
          this.setActive(null);
        });
        bundle.marker.on("click", (e) => {
          L.DomEvent.stopPropagation(e);
          this.$emit("marker-click", { id: item.id, type: item.type, item });
        });

        this.attachTooltip(item, bundle.marker);

        if (bundle.accuracyCircle) {
          // Ensure accuracy circle doesn't block zoom - force pointer-events after adding
          this.rootGroup.addLayer(bundle.accuracyCircle);
          // Force disable pointer events immediately - use multiple methods to ensure it works
          const disablePointerEvents = () => {
            if (bundle.accuracyCircle) {
              // Method 1: Direct DOM manipulation
              const path = bundle.accuracyCircle._path;
              if (path) {
                path.style.pointerEvents = 'none';
                path.style.cursor = 'default';
              }
              // Method 2: Ensure options are set
              bundle.accuracyCircle.options.interactive = false;
              bundle.accuracyCircle.options.bubblingMouseEvents = false;
              // Method 3: Remove any event listeners
              bundle.accuracyCircle.off();
            }
          };
          // Try immediately and in next tick
          disablePointerEvents();
          this.$nextTick(disablePointerEvents);
          // Also listen for when circle is added to map
          bundle.accuracyCircle.on('add', disablePointerEvents);
        }
        this.rootGroup.addLayer(bundle.marker);
        // Wrap the marker/accuracyCircle bundle so Vue's Map proxy doesn't
        // recursively wrap the Leaflet instances when it lands in markersById.
        this.markersById.set(item.id, markRaw(bundle));
      });

      // Only fit to items on initial load, not on every rebuild (prevents issues during repeated zooming)
      if (this.isInitialLoad && deduped.length && this.map?._panes?.mapPane) {
        this.fitToItems();
        this.isInitialLoad = false;
      }
      // if a row is already hovered when we rebuilt, re-apply highlight
      if (this.highlightId) this.setActive(this.highlightId);
    },

    normalizeItem(it) {
      if (Number.isFinite(it.lat) && Number.isFinite(it.lng)) return it;
      if (Number.isFinite(it.xLocal) && Number.isFinite(it.yLocal)) {
        const { lat, lng } = toLatLng({ x: it.xLocal, y: it.yLocal }, this.selectedCrs);
        return { ...it, lat, lng };
      }
      return null;
    },

    // Retry tiles that fail to load (one-shot, with a short delay).
    // Critical for slow rate-limited servers like OpenTopoMap, where
    // zoom-during-load aborts produce permanent gaps without this.
    attachTileRetry(layer) {
      if (!layer || !layer.on) return;
      const retried = new WeakSet();
      layer.on("tileerror", (e) => {
        const tile = e.tile;
        if (!tile || retried.has(tile)) return;
        retried.add(tile);
        const originalSrc = tile.src;
        if (!originalSrc) return;
        setTimeout(() => {
          // Cache-bust so the browser doesn't return the cached failure
          try {
            tile.src = originalSrc + (originalSrc.includes("?") ? "&" : "?") + "_r=" + Date.now();
          } catch {}
        }, 1500);
      });
    },

    fitToItems(pad = 0.05) {
      if (!this.map) return;

      // Use group bounds first (includes clusters), else fall back to marker coords
      let bounds = this.rootGroup?.getBounds?.();
      if (!bounds || !bounds.isValid()) {
        const coords = [];
        this.markersById.forEach(({ marker }) => {
          const ll = marker.getLatLng();
          coords.push([ll.lat, ll.lng]);
        });
        if (!coords.length) return;
        bounds = L.latLngBounds(coords);
      }

      // Helper function to check if map is fully ready
      const isMapReady = () => {
        return this.map && 
               this.map._container && 
               this.map._panes && 
               this.map._panes.mapPane &&
               this.map._renderer &&
               this.map._renderer._container;
      };

      // Wait for map to be ready before fitting bounds
      this.map.whenReady(() => {
        // Double-check renderer is ready with a small delay if needed
        const attemptFit = () => {
          if (!isMapReady()) {
            // If not ready, try again after a short delay
            setTimeout(attemptFit, 50);
            return;
          }

          try {
            const canAnimate = this.map.options.zoomAnimation !== false && this.map._loaded;
            this.map.fitBounds(bounds.pad(pad), {
              animate: canAnimate,
              padding: [20, 20],
              maxZoom: 18,
            });
          } catch (e) {
            // Silently fail if fitBounds still errors
            console.debug('fitBounds error (map not fully ready):', e);
          }
        };

        attemptFit();
      });
    },

    setActive(id) {
      if (this.activeId && this.markersById.has(this.activeId)) {
        const el = this.markersById.get(this.activeId).marker.getElement();
        if (el) el.classList.remove("is-hot");
        this.markersById.get(this.activeId).marker.setZIndexOffset(0);
      }
      this.activeId = id || null;

      if (id && this.markersById.has(id)) {
        const m = this.markersById.get(id).marker;
        const el = m.getElement();
        if (el) el.classList.add("is-hot");
        m.setZIndexOffset(1000);
      }
    },

    highlight(id) {
      const bundle = this.markersById.get(id);
      if (!bundle) return;
      const el = bundle.marker.getElement();
      if (!el) return;
      el.classList.add("is-hot");
      bundle.marker.setZIndexOffset(1000);
      setTimeout(() => {
        el.classList.remove("is-hot");
        bundle.marker.setZIndexOffset(0);
      }, 1000);
    },
    buildImportedPopup(props) {
      if (!props) return "";
      const esc = (s) =>
        String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
      const name = props.name || props.Name || props.title || "";
      const rows = [];
      const add = (label, val) => {
        if (val !== undefined && val !== null && String(val).trim() !== "")
          rows.push(`<div><strong>${label}:</strong> ${esc(val)}</div>`);
      };
      add("Category", props.category);
      add("Type", props.wwCategory);
      add("Source", props.source);
      add("Ref", props.externalId);
      if (!name && rows.length === 0) return "";
      const title = name ? `<div style="font-weight:600;margin-bottom:3px;">${esc(name)}</div>` : "";
      return `<div style="min-width:150px;font-size:12px;line-height:1.4;">${title}${rows.join("")}</div>`;
    },
    loadGeojson(geojson) {
      if (!geojson || !this.map) return;

      // Ensure map is ready before proceeding
      if (!this.map._container) {
        this.map.whenReady(() => {
          this.loadGeojson(geojson);
        });
        return;
      }

      if (this.geojsonLayer) {
        this.geojsonLayer.removeFrom(this.map);
        this.geojsonLayer = null;
        this.geoLayerById.clear();
        // Remove all zone labels
        this.geoLabelById.forEach((label) => {
          if (label && this.map) {
            this.map.removeLayer(label);
          }
        });
        this.geoLabelById.clear();
      }

      const baseStyle = (feature) => ({
        color: feature?.properties?.colour || "#ff0000",
        weight: 2,
        fillOpacity: 0.15,
      });
      const hoverStyle = { weight: 4, fillOpacity: 0.25 };

      this.geojsonLayer = markRaw(L.geoJSON(geojson, {
        style: baseStyle,
        pointToLayer: (feature, latlng) =>
          L.circleMarker(latlng, {
            radius: 5,
            fillColor: feature?.properties?.colour || "#ff0000",
            color: "#000",
            weight: 1,
          }),
        onEachFeature: (feature, layer) => {
          // Pick your key: prefer a stable numeric/string id
          const key =
            feature?.properties?.zoneId ??
            feature?.properties?.ZoneId ??
            feature?.properties?.id ??
            feature?.properties?.name; // fallback

          if (key != null) this.geoLayerById.set(String(key), layer);

          // Imported overlay features (Outdooractive / OuterSpatial) — points AND trail lines —
          // get a detail popup and must NOT trigger the zone/inspection click flow.
          const props = feature?.properties ?? {};
          const isImported =
            props.source === "Outdooractive" || props.source === "OuterSpatial";
          if (isImported) {
            const html = this.buildImportedPopup(props);
            if (html) layer.bindPopup(html);
          }

          // Add zone name label if enabled and zone name exists (not for imported overlays)
          if (this.showZoneLabels && !isImported && feature?.geometry?.type !== "Point") {
            const zoneName =
              feature?.properties?.zoneName ??
              feature?.properties?.ZoneName ??
              feature?.properties?.name ??
              "";

            if (zoneName) {
              this.addZoneLabel(feature, layer, zoneName, key);
            }
          }

          layer.on("mouseover", () => {
            layer.setStyle(hoverStyle);
            this.$emit("shape-hover", {
              id: key ?? null,
              properties: feature?.properties ?? {},
              feature,
            });
          });
          layer.on("mouseout", () => {
            layer.setStyle(baseStyle(feature));
            this.$emit("shape-leave", {
              id: key ?? null,
              properties: feature?.properties ?? {},
              feature,
            });
          });
          layer.on("click", () => {
            // For imported overlay features (points & trails), just show the popup — don't open inspections.
            if (isImported) return;
            this.$emit("shape-click", {
              id: key ?? null,
              properties: feature?.properties ?? {},
              feature,
            });
          });
        },
      }).addTo(this.map));

      // Only fit on the FIRST geojson load. Otherwise toggling any feature
      // layer (Trail Issues, Land Parcels, etc.) re-runs this with the same
      // zone data and yanks the user back to the zones' extent.
      if (this.isInitialGeojsonLoad) {
        const bounds = this.geojsonLayer.getBounds?.();
        if (bounds && bounds.isValid() && this.map) {
          this.map.whenReady(() => {
            try {
              if (this.map && this.map._container && this.map._panes) {
                const canAnimate = this.map.options.zoomAnimation !== false && this.map._loaded;
                this.map.fitBounds(bounds, {
                  padding: [20, 20],
                  animate: canAnimate,
                });
              }
            } catch (e) {
              // Silently ignore; first-load fit is best-effort.
            }
          });
        }
        this.isInitialGeojsonLoad = false;
      }
    },
    addZoneLabel(feature, layer, zoneName, key) {
      try {
        // Get the center of the polygon/polyline
        let center;
        if (layer.getBounds) {
          const bounds = layer.getBounds();
          center = bounds.getCenter();
        } else if (feature.geometry.type === "Point") {
          center = L.latLng(feature.geometry.coordinates[1], feature.geometry.coordinates[0]);
        } else {
          // Fallback: calculate center from coordinates
          const coords = this.extractCoordinates(feature.geometry);
          if (coords.length === 0) return;

          let sumLat = 0;
          let sumLng = 0;
          coords.forEach(([lat, lng]) => {
            sumLat += lat;
            sumLng += lng;
          });
          center = L.latLng(sumLat / coords.length, sumLng / coords.length);
        }

        // Create a label with zone name
        const labelHtml = `
          <div class="zone-label" style="
            background: rgba(255, 255, 255, 0.9);
            border: 2px solid ${feature?.properties?.colour || "#ff0000"};
            border-radius: 4px;
            padding: 4px 8px;
            font-size: 12px;
            font-weight: 600;
            color: #333;
            white-space: nowrap;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            pointer-events: none;
            text-align: center;
          ">
            ${this.escapeHtml(zoneName)}
          </div>
        `;

        const labelIcon = L.divIcon({
          html: labelHtml,
          className: "zone-label-container",
          iconSize: [100, 30], // Approximate, will auto-size
          iconAnchor: [50, 15],
        });

        const labelMarker = markRaw(L.marker(center, {
          icon: labelIcon,
          interactive: false,
          zIndexOffset: -100, // Behind the polygon
        }).addTo(this.map));

        if (key != null) {
          this.geoLabelById.set(String(key), labelMarker);
        }
      } catch (err) {
      }
    },
    extractCoordinates(geometry) {
      const coords = [];
      if (!geometry || !geometry.coordinates) return coords;

      const processCoords = (coordArray) => {
        if (Array.isArray(coordArray[0])) {
          coordArray.forEach((item) => processCoords(item));
        } else if (coordArray.length >= 2) {
          // [lng, lat] format
          coords.push([coordArray[1], coordArray[0]]);
        }
      };

      processCoords(geometry.coordinates);
      return coords;
    },

    setActiveShape(id) {
      // clear previous
      if (this.activeShapeId != null) {
        const prev = this.geoLayerById.get(String(this.activeShapeId));
        if (prev) {
          prev.setStyle({
            weight: 2,
            fillOpacity: 0.15,
          });
        }
      }
      this.activeShapeId = id ?? null;

      // apply highlight
      if (id != null) {
        const lyr = this.geoLayerById.get(String(id));
        if (lyr) {
          lyr.setStyle({ weight: 4, fillOpacity: 0.25 });
          // Optionally bring to front
          if (lyr.bringToFront) lyr.bringToFront();
        }
      }
    },

    highlightShapeOnce(id, ms = 800) {
      this.setActiveShape(id);
      if (id != null) {
        setTimeout(() => this.setActiveShape(null), ms);
      }
    },
    escapeHtml(s = "") {
      return String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },
    fmt(n, d = 6) {
      const v = Number(n);
      return Number.isFinite(v) ? v.toFixed(d) : "";
    },
    osgbAlphaGrid(e, n) {
      // OSGB helper (same as your component’s)
      if (e < 0 || e > 700000 || n < 0 || n > 1300000) {
        return `${Math.round(e)} ${Math.round(n)}`;
      }
      const e100k = Math.floor(e / 100000);
      const n100k = Math.floor(n / 100000);
      const l1 = 19 - n100k - ((19 - n100k) % 5) + Math.floor((e100k + 10) / 5);
      const l2 = (((19 - n100k) * 5) % 25) + (e100k % 5);
      const toLetter = (l) => String.fromCharCode(l + 65 + (l > 7 ? 1 : 0));
      const letters = `${toLetter(l1)}${toLetter(l2)}`;
      const eR = Math.round(e % 100000);
      const nR = Math.round(n % 100000);
      const pad = (num) => num.toString().padStart(5, "0");
      return `${letters} ${pad(eR)} ${pad(nR)}`;
    },

    projectedStringFromSettings(lat, lng, latLngDecimals = 6, projectedDecimals = 2) {
      // Safety
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "";

      const crs = this.settingsStore?.crs || {};
      const epsg = Number(crs.code ?? 4326);
      const proj4Def = crs.proj4Def || "+proj=longlat +datum=WGS84 +no_defs";

      // Ensure base
      if (!proj4.defs("EPSG:4326")) {
        proj4.defs("EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs");
      }
      // Ensure target
      const key = `EPSG:${epsg}`;
      if (!proj4.defs(key)) {
        proj4.defs(key, proj4Def);
      }

      try {
        if (epsg === 4326) {
          // Just echo lat/lng nicely
          return `Lat/Lng: ${this.fmt(lat, latLngDecimals)}, ${this.fmt(lng, latLngDecimals)}`;
        }
        // proj4 expects [lon, lat]
        const [x, y] = proj4("EPSG:4326", key, [lng, lat]);

        // Formatting by CRS
        if (epsg === 2157) {
          // Irish ITM
          return `E ${this.fmt(x, projectedDecimals)}  N ${this.fmt(y, projectedDecimals)}`;
        }
        if (epsg === 27700) {
          // OSGB: alphanumeric (grid ref)
          return this.osgbAlphaGrid(x, y);
        }
        // Generic projected
        return `${this.fmt(x, projectedDecimals)}, ${this.fmt(y, projectedDecimals)}`;
      } catch {
        return "";
      }
    },

    async loadFeatureLayers() {
      try {
        // Ensure map is ready
        if (!this.map) {
          setTimeout(() => this.loadFeatureLayers(), 200);
          return;
        }

        // Wait for map to be fully ready before loading layers
        await new Promise((resolve) => {
          if (this.map._loaded) {
            resolve();
          } else {
            this.map.whenReady(() => {
              this.map._loaded = true;
              resolve();
            });
          }
        });

        // Load all map overlays from database
        const overlayData = await getLookupDataByType("MapOverlay");


        if (!overlayData || overlayData.length === 0) {
          return;
        }

        // Include ALL overlays (active + inactive) so disabled ones stay on screen and can be re-enabled
        const featureLayersToLoad = overlayData.filter(o => {
          const mapType = o.value || o.mapType || '';
          const isFeature = mapType === 'feature';
          const isTile = mapType === 'tile';
          return isFeature || isTile;
        });

        if (featureLayersToLoad.length === 0) {
          return;
        }

        // Process each feature layer and store by lookupDataId
        featureLayersToLoad.forEach(layer => {
          const layerName = layer.name;
          const featureUrl = layer.tileUrl; // TileUrl contains FeatureServer URL for features
          const styleColor = layer.colour || '#3388ff';

          // Special debugging for National Trail layer
          const isNationalTrail = layerName.includes('NATIONAL TRAILS') || layerName.includes('National Trail');
          if (isNationalTrail) {
          }


          if (!featureUrl) {
            if (isNationalTrail) {
            }
            return;
          }

          // Check if it's a tile layer or feature layer FIRST (before using isTile)
          const mapType = layer.value || layer.mapType || '';
          const isTile = mapType === 'tile';

          // Validate FeatureServer URL format for feature layers
          if (!isTile && featureUrl) {
            const isValidFeatureServer = featureUrl.includes('/FeatureServer/') || featureUrl.includes('/featureServer/');
            if (!isValidFeatureServer && !featureUrl.includes('/MapServer/tile/')) {
              if (isNationalTrail) {
              }
            } else if (isValidFeatureServer && isNationalTrail) {
            }
          }

          let leafletLayer;

          if (isTile) {
            // TILE LAYER: Base maps are implemented as map tiles (pre-rendered images)
            // These use the {z}/{x}/{y} pattern and are static at each zoom level
            // Examples: OSM GPS traces, satellite imagery tiles
            leafletLayer = L.tileLayer(featureUrl, {
              attribution: '© OpenStreetMap contributors',
              maxZoom: 20,
              subdomains: 'abc'
            });
          } else {
            // FEATURE LAYER: Should be implemented as vector/GeoJSON via FeatureServer query endpoint
            // NOT as tiles - these are dynamic vector features that scale with zoom
            // esri-leaflet featureLayer automatically uses the query endpoint, not tile endpoint
            let style = {
              color: styleColor,
              weight: 3,
              fillOpacity: 0.6
            };

            // Special styling for specific layers
            if (layerName.includes('Trail Issue') || layerName.includes('Trail Issues')) {
              style.color = '#FF6347';
              style.weight = 3;
              style.fillOpacity = 0.6;
            } else if (layerName.includes('NATIONAL TRAILS') || layerName.includes('National Trail')) {
              style.weight = 4;
              style.fillOpacity = 0.8;
              style.dashArray = '5, 10';
              style.color = styleColor || '#FF8C00'; // Orange color for trails
            } else if (layerName.includes('SAC') || layerName.includes('SPA')) {
              style.weight = 2;
              style.fillOpacity = 0.3;
            }

            // Feature layers should be implemented as vector/GeoJSON layers via FeatureServer query endpoint
            // NOT as tile layers - esri-leaflet featureLayer handles this automatically
            leafletLayer = featureLayer({
              url: featureUrl,
              style: style,
              useCors: true,
              // Ensure feature layer uses query endpoint, not tile endpoint
              // This ensures proper zoom behavior as vector data, not pre-rendered tiles
              simplifyFactor: 1, // No simplification for better accuracy
              precision: 5, // Coordinate precision
              onEachFeature: (feature, layer) => {
                let title = layerName;
                // Create popup title based on layer
                if (layerName.includes('Registration')) title = '📋 Registration Survey';
                else if (layerName.includes('Trail Issue')) title = '⚠️ Trail Issue';
                else if (layerName.includes('Trailhead')) title = '🏃 Trailhead Inspection';
                else if (layerName.includes('NATIONAL TRAILS')) title = '🏔️ National Trail';
                else if (layerName.includes('SAC')) title = '🛡️ SAC: ' + (feature.properties?.SITENAME || '');
                else if (layerName.includes('SPA')) title = '🕊️ SPA: ' + (feature.properties?.SITENAME || feature.properties?.SiteName || '');

                // Create simple popup (wrapped in scrollable container)
                if (feature.properties) {
                  let popupContent = `<strong>${title}</strong><br><div class="map-popup-scrollable">`;
                  Object.keys(feature.properties).forEach(key => {
                    const value = feature.properties[key];
                    if (value !== null && value !== undefined && value !== '') {
                      const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                      popupContent += `<strong>${formattedKey}:</strong> ${value}<br>`;
                    }
                  });
                  popupContent += '</div>';
                  layer.bindPopup(popupContent, {
                    maxWidth: 400,
                    minWidth: 350,
                    className: 'map-popup-scrollable-wrapper'
                  });
                } else {
                  layer.bindPopup(title);
                }
              }
            });

            // Ensure feature layer properly handles zoom events
            // Feature layers should refresh on zoom to load features for current viewport
            if (leafletLayer && this.map) {
              // Feature layers from esri-leaflet automatically handle zoom/pan events
              // but we ensure they're properly configured as overlays, not base layers
              leafletLayer.on('loading', () => {
                if (isNationalTrail) {
                } else {
                }
              });

              leafletLayer.on('load', () => {
                if (isNationalTrail) {
                  // Try to get bounds to verify data loaded
                  try {
                    if (leafletLayer.getBounds) {
                      const bounds = leafletLayer.getBounds();
                    }
                  } catch (e) {
                  }
                } else {
                }
              });

              // Add specific error handling for National Trail
              if (isNationalTrail) {
                leafletLayer.on('error', (e) => {
                });
              }
            }
          }

          // Store by lookupDataId for toggling (store as both string and number for compatibility)
          const lookupDataId = layer.lookupDataId;
          const idStr = String(lookupDataId);
          const idNum = Number(lookupDataId);

          this.featureLayers[lookupDataId] = markRaw(leafletLayer);
          this.featureLayersById[lookupDataId] = markRaw({
            layer: leafletLayer,
            name: layerName,
            data: layer
          });
          // Also store with string key for compatibility
          if (idStr !== String(lookupDataId)) {
            this.featureLayersById[idStr] = this.featureLayersById[lookupDataId];
          }

          if (isNationalTrail) {
          } else {
          }

          // Add error handling for feature layer loading (tile layers handle errors differently)
          if (leafletLayer && leafletLayer.on && !isTile) {
            leafletLayer.on('error', (e) => {
              if (isNationalTrail) {
              } else {
              }
            });
          }
        });

        this.featureLayersLoaded = true;

        // Process any pending updates
        if (this.pendingFeatureLayerUpdates.length > 0) {
          this.pendingFeatureLayerUpdates.forEach(ids => {
            this.updateFeatureLayers(ids);
          });
          this.pendingFeatureLayerUpdates = [];
        }

        // Emit event that layers are loaded
        this.$emit("feature-layers-loaded", Object.keys(this.featureLayersById));

      } catch (error) {
        this.featureLayersLoaded = true; // Set to true even on error to prevent infinite waiting
      }
    },

    updateFeatureLayers(enabledIds) {

      // If layers aren't loaded yet, queue this update
      if (!this.featureLayersLoaded) {
        this.pendingFeatureLayerUpdates.push(enabledIds);
        return;
      }

      if (!this.map) {
        return;
      }

      // Remove all feature layers from map first
      // Use layer.remove() - more reliable than map.removeLayer() for esri-leaflet layers
      Object.values(this.featureLayers).forEach(layer => {
        if (layer && typeof layer.remove === 'function') {
          try {
            layer.remove();
          } catch (e) {
            if (this.map && this.map.removeLayer) this.map.removeLayer(layer);
          }
        }
      });

      // Add enabled feature layers to map as overlays
      // These are vector layers that will automatically refresh on zoom/pan
      // (esri-leaflet featureLayer handles this via FeatureServer query endpoint)
      if (Array.isArray(enabledIds) && enabledIds.length > 0) {
        enabledIds.forEach(id => {
          // Try both string and number versions of the ID
          const idStr = String(id);
          const idNum = Number(id);
          const layerData = this.featureLayersById[idStr] || this.featureLayersById[idNum] || this.featureLayersById[id];

          if (layerData && layerData.layer && this.map) {
            try {
              const isNationalTrail = layerData.name.includes('NATIONAL TRAILS') || layerData.name.includes('National Trail');
              if (!this.map.hasLayer(layerData.layer)) {
                // Add as overlay - feature layers don't interfere with base map zoom
                layerData.layer.addTo(this.map);
                if (isNationalTrail) {
                } else {
                }
              } else if (isNationalTrail) {
              }
            } catch (e) {
              const isNationalTrail = layerData.name.includes('NATIONAL TRAILS') || layerData.name.includes('National Trail');
              if (isNationalTrail) {
              } else {
              }
            }
          } else {
            const availableIds = Object.keys(this.featureLayersById).join(', ');

            // Check if it might be National Trail
            const allLayers = Object.values(this.featureLayersById);
            const nationalTrailLayer = allLayers.find(l => l.name.includes('NATIONAL TRAILS') || l.name.includes('National Trail'));
            if (nationalTrailLayer) {
            }
          }
        });
      } else {
      }
    },
  },
};

function escapeHtml(s = "") {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
</script>
<style scoped>
.waymap {
  width: 100%;
  height: 100%; /* tweak as you like */
  border-radius: 10px;
  overflow: hidden;
  display: block;
  position: relative;
}
.waymap__map {
  width: 100%;
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
}
/* Make the Leaflet container transparent */
:deep(.leaflet-div-icon) {
  background: transparent !important;
  border: none !important;
}

:deep(.leaflet-top.leaflet-left .leaflet-control-zoom) {
  margin-top: 18px !important;
  margin-left: 18px !important;
  z-index: 2500 !important;
}

/* Marker shell */
:deep(.wm .wm-circle) {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid transparent;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9);
  position: relative;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}

:deep(.wm.is-hot .wm-circle) {
  transform: scale(1.18);
  box-shadow: 0 0 0 2px #fff, 0 0 0 6px rgba(0, 0, 0, 0.18);
}

/* Center icon (Font Awesome) */
:deep(.wm .wm-fa) {
  color: #fff; /* <-- white FA icon */
  font-size: 16px;
  line-height: 16px;
}

/* Center image (your shovel.png) */
:deep(.wm .wm-img) {
  width: 18px;
  height: 18px;
  display: block;
  object-fit: contain;
  background: transparent;
}

/* Optional small badge */
:deep(.wm .wm-badge) {
  position: absolute;
  bottom: -4px;
  right: -4px;
  background: #111;
  color: #fff;
  border-radius: 10px;
  padding: 0 5px;
  font-size: 11px;
  line-height: 16px;
  height: 16px;
  min-width: 16px;
  text-align: center;
  box-shadow: 0 0 0 2px #fff;
}
:deep(.wm-stack-icon) {
  background: transparent;
  border: none;
}
:deep(.wm-stack) {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  font: 600 12px/1 system-ui, sans-serif;
  color: #fff;
  background: rgba(30, 41, 59, 0.9); /* slate-ish */
  box-shadow: 0 0 0 2px #fff, 0 6px 14px rgba(0, 0, 0, 0.25);
}

/* Tooltip container (keep snappy) */
:deep(.leaflet-tooltip) {
  transition: none !important;
  overflow: visible; /* allow content to grow outside the base box if needed */
}

/* Our custom tooltip skin */
:deep(.wm-tip.leaflet-tooltip) {
  background: transparent;
  border: none;
  padding: 0;
  filter: drop-shadow(0 6px 18px rgba(0, 0, 0, 0.18));
  pointer-events: none;
}

/* Card */
:deep(.wm-tip-card) {
  /* was: min-width: 220px; max-width: 280px; */
  min-width: 240px;
  max-width: clamp(280px, 60vw, 480px); /* responsive cap */
  background: #fff;
  border-radius: 12px;
  padding: 10px 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

/* Title/subtitle */
:deep(.wm-tip-title) {
  font-weight: 700;
  font-size: 14px;
  line-height: 1.2;
  margin-bottom: 4px;
  color: #0f172a;
  word-wrap: break-word; /* wrap long titles */
  overflow-wrap: anywhere; /* force break if needed */
}
:deep(.wm-tip-sub) {
  font-size: 12px;
  color: #475569;
  margin-bottom: 6px;
}

/* Rows */
:deep(.wm-tip-row) {
  display: grid; /* grid handles wrapping better than flex for labels/values */
  grid-template-columns: auto 1fr;
  column-gap: 10px;
  row-gap: 2px;
  font-size: 12px;
  align-items: start; /* keep label top-aligned when value wraps */
}

/* Labels */
:deep(.wm-tip-label) {
  color: #64748b;
  white-space: nowrap; /* labels never wrap */
}

/* Values (wrap allowed) */
:deep(.wm-tip-val) {
  color: #0f172a;
  font-weight: 600;
  white-space: normal; /* allow wrapping */
  overflow-wrap: anywhere; /* ensure break on long tokens */
}

/* Optional: monospace just for numeric blocks (add class="mono" where you output values) */
:deep(.wm-tip-val.mono) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace;
}

/* Type badge */
:deep(.wm-badge-mini) {
  display: inline-block;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 999px;
  color: #fff;
  margin-right: 6px;
  text-transform: capitalize;
}
:deep(.wm-badge-mini.finding) {
  background: #f2994a;
}
:deep(.wm-badge-mini.task) {
  background: #2a72cf;
}
:deep(.wm-badge-mini.inspection) {
  background: #27ae60;
}
:deep(.wm-badge-mini.asset) {
  background: #9c27b0;
}
:deep(.wm-badge-mini.iot) {
  background: #00897b;
}

/* Ensure accuracy circles don't block zoom interactions */
/* Accuracy circles (created with interactive: false) should not capture mouse events */
:deep(.leaflet-interactive.leaflet-circle),
:deep(.leaflet-interactive.leaflet-circle *),
:deep(path[class*="leaflet-circle"]),
:deep(svg[class*="leaflet-circle"]) {
  pointer-events: none !important;
  cursor: default !important;
}

/* Markers should remain interactive */
:deep(.leaflet-marker-icon),
:deep(.leaflet-marker-icon *) {
  pointer-events: auto !important;
}

/* Ensure map container itself allows zoom */
.waymap__map {
  pointer-events: auto !important;
}

.waymap__map :deep(.leaflet-container) {
  pointer-events: auto !important;
}


/* Scrollable popup for feature layers (trail issues, etc.) */
:deep(.map-popup-scrollable-wrapper .leaflet-popup-content) {
  max-height: min(70vh, 500px);
  overflow: hidden;
}

:deep(.map-popup-scrollable) {
  max-height: min(55vh, 420px);
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
}

:deep(.map-popup-scrollable::-webkit-scrollbar) {
  width: 6px;
}

:deep(.map-popup-scrollable::-webkit-scrollbar-thumb) {
  background: #c1c1c1;
  border-radius: 3px;
}
</style>
